/**
 * T+ Signals Panel — clean table layout
 */
import React, { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, CircularProgress, alpha,
} from '@mui/material';
import { ShowChart, AddCircleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const thSx = { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.02em', py: 0.8, borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc' };
const tdSx = { py: 0.8, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

const SignalsPanel = ({ scanResult, formatNumber, formatPercent, onCreatePosition }) => {
  const { t } = useTranslation();
  const [loadingTicker, setLoadingTicker] = useState(null);

  const handleEntry = async (signal) => {
    if (loadingTicker) return;
    setLoadingTicker(signal.ticker);
    try {
      const today = scanResult.scan_date || new Date().toISOString().slice(0, 10);
      await onCreatePosition?.({
        ticker: signal.ticker,
        entry_date: today,
        signal_date: today,
        setup_type: signal.setup_type,
        entry_price: signal.entry_price,
        stop_price: signal.stop_price,
        tp1_price: signal.tp1_price,
        tp2_price: signal.tp2_price,
        atr_percent: signal.atr_percent,
        confidence_score: signal.confidence_score,
        market_regime: signal.market_regime,
        position_size_pct: signal.position_size_pct,
        sector: signal.sector,
      });
    } finally {
      setLoadingTicker(null);
    }
  };
  if (!scanResult || !scanResult.signals || scanResult.signals.length === 0) {
    return (
      <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ShowChart sx={{ fontSize: 24, color: '#cbd5e1' }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8', lineHeight: 1.4 }}>
            {t('tplus.signals.emptyTitle')}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3, maxWidth: 420 }}>
            {t('tplus.signals.emptyDesc')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={thSx}>{t('tplus.header.ticker')}</TableCell>
            <TableCell sx={thSx}>Setup</TableCell>
            <TableCell align="right" sx={thSx}>{t('tplus.header.entry')}</TableCell>
            <TableCell align="right" sx={thSx}>Stop</TableCell>
            <TableCell align="right" sx={thSx}>TP1</TableCell>
            <TableCell align="right" sx={thSx}>TP2</TableCell>
            <TableCell align="center" sx={thSx}>{t('tplus.header.confidence')}</TableCell>
            <TableCell align="center" sx={thSx}>ML</TableCell>
            <TableCell align="right" sx={thSx}>Size</TableCell>
            <TableCell sx={thSx}>{t('tplus.header.sector')}</TableCell>
            <TableCell align="center" sx={thSx}>{t('tplus.header.action')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {scanResult.signals.map((s, i) => {
            const setupClr = s.setup_type === 'Spring' ? '#10b981' : s.setup_type === 'NarrowSpring' ? '#059669' : s.setup_type === 'MAConverge' ? '#0ea5e9' : s.setup_type === 'GapAndGo' ? '#8b5cf6' : s.setup_type === 'ConfirmBreak' ? '#f59e0b' : '#3b82f6';
            const confClr = s.confidence_score >= 60 ? '#10b981' : s.confidence_score >= 50 ? '#f59e0b' : '#ef4444';
            const mlScore = s.ml_score;
            const mlClr = mlScore >= 0.70 ? '#10b981' : mlScore >= 0.55 ? '#3b82f6' : '#94a3b8';
            return (
              <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                <TableCell sx={tdSx}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', color: '#1e293b' }}>{s.ticker}</Typography>
                </TableCell>
                <TableCell sx={tdSx}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: setupClr }}>{t(`tplus.setup.${s.setup_type}`, s.setup_type)}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600 }}>{formatNumber(s.entry_price)}</TableCell>
                <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#ef4444' }}>{formatNumber(s.stop_price)}</TableCell>
                <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#10b981' }}>{formatNumber(s.tp1_price)}</TableCell>
                <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#10b981' }}>{formatNumber(s.tp2_price)}</TableCell>
                <TableCell align="center" sx={tdSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 32, height: 5, bgcolor: '#e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                      <Box sx={{ width: `${s.confidence_score}%`, height: '100%', bgcolor: confClr, borderRadius: 1 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: 'monospace', color: confClr, minWidth: 22 }}>{s.confidence_score.toFixed(0)}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="center" sx={tdSx}>
                  {mlScore != null ? (
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: 'monospace', color: mlClr }}>{(mlScore * 100).toFixed(0)}</Typography>
                  ) : (
                    <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>–</Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 600, color: '#64748b' }}>{formatPercent(s.position_size_pct)}</TableCell>
                <TableCell sx={{ ...tdSx, color: '#94a3b8' }}>{s.sector || '–'}</TableCell>
                <TableCell align="center" sx={tdSx}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={loadingTicker === s.ticker}
                    onClick={() => handleEntry(s)}
                    startIcon={loadingTicker === s.ticker ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutline sx={{ fontSize: 15 }} />}
                    sx={{
                      px: 1.5, py: 0.3, fontSize: '0.72rem', fontWeight: 700,
                      textTransform: 'none', borderRadius: 1.5, minWidth: 0,
                      background: `linear-gradient(135deg, ${setupClr} 0%, ${alpha(setupClr, 0.8)} 100%)`,
                      boxShadow: `0 2px 6px ${alpha(setupClr, 0.25)}`,
                      '&:hover': { boxShadow: `0 3px 10px ${alpha(setupClr, 0.35)}` },
                    }}
                  >
                    {t('tplus.signals.enterBtn')}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SignalsPanel;
